import {
  Box,
  Button,
  IconButton,
  Stack,
  Typography,
  CircularProgress,
  Paper,
  Breadcrumbs,
  Tooltip,
  alpha,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Toolbar,
  InputAdornment,
  OutlinedInput,
  LinearProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { FC, useState, useRef, useCallback, useMemo, useEffect } from 'react';

import {
  TDocument,
  TDocumentStatus,
  TDocumentMetadata,
  UploadStreamResponse,
} from '@/types/document';
import { TDocumentFolder } from '@/types/document-folder';

import MuiPageWrapper from '@/@mui/MuiPageWrapper';
import {
  useGetFolderHierarchy,
  useGetFolderContents,
  useGetRootContents,
  useMoveDocumentsToFolder,
  useDeleteFolder,
} from '@/api/document-folder';
import { useMoveDocumentsBetweenProperties } from '@/api/endpoints/document/useMoveDocumentsBetweenProperties';
import { useMoveFoldersBetweenProperties } from '@/api/endpoints/document/useMoveFoldersBetweenProperties';
import { useGetAllPropertyLocations } from '@/api/endpoints/propertyLocation/useGetAllPropertyLocations';
import { useDeleteVaultDocument } from '@/api/endpoints/vault/useDeleteVaultDocument';
import { ArrowRightIcon } from '@/assets/icons/ArrowRightIcon';
import { Building03Icon } from '@/assets/icons/Building03Icon';
import { DocumentIcon } from '@/assets/icons/DocumentIcon';
import { EyeIcon } from '@/assets/icons/EyeIcon';
import { File07Icon } from '@/assets/icons/File07Icon';
import { FolderIcon } from '@/assets/icons/FolderIcon';
import { FolderPlusIcon } from '@/assets/icons/FolderPlusIcon';
import { FolderShieldIcon } from '@/assets/icons/FolderShieldIcon';
import { Image01Icon } from '@/assets/icons/Image01Icon';
import { MoveIcon } from '@/assets/icons/MoveIcon';
import { SearchMdIcon } from '@/assets/icons/SearchMdIcon';
import { Trash01Icon } from '@/assets/icons/Trash01Icon';
import { UploadCloud01Icon } from '@/assets/icons/UploadCloud01Icon';
import { useDocumentUploadV2 } from '@/hooks/useDocumentUploadV2';

import DocumentViewModal from '@/components/document/DocumentViewModal';

import CreateFolderModal from './CreateFolderModal';
import FileIconWithOverlay from './FileIconWithOverlay';

type Order = 'asc' | 'desc';

interface SortableItem {
  id: string | number;
  name: string;
  created_at: string;
  size?: number;
  _count?: { documents: number };
  type: 'folder' | 'document';
  mime_type?: string;
  original_name?: string;
  is_system_folder?: boolean;
  file_path?: string;
  status?: string;
  created_by: string;
  updated_at: string;
  lastModifiedDate?: string | null;
  documentCount?: number;
  metadata?: TDocumentMetadata;
  description?: string;
}

interface QueueItem {
  file: File;
  targetFolderId: number | null;
}

interface ContextMenuState {
  mouseX: number;
  mouseY: number;
  item: SortableItem | null;
  open: boolean;
}

interface MoveSubmenuState {
  open: boolean;
  anchorEl: HTMLElement | null;
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] === null || b[orderBy] === undefined) return -1;
  if (a[orderBy] === null || a[orderBy] === undefined) return 1;

  if (typeof a[orderBy] === 'string') {
    const aValue = String(a[orderBy]).toLowerCase();
    const bValue = String(b[orderBy]).toLowerCase();
    if (bValue < aValue) return -1;
    if (bValue > aValue) return 1;
    return 0;
  }

  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator<T>(order: Order, orderBy: keyof T) {
  return order === 'desc'
    ? (a: T, b: T) => descendingComparator(a, b, orderBy)
    : (a: T, b: T) => -descendingComparator(a, b, orderBy);
}

interface FolderViewProps {
  currentFolderId: number | null;
  propertyLocationId?: number;
  onFolderChange?: (folderId: number | null) => void;
}

const FolderView: FC<FolderViewProps> = ({
  currentFolderId,
  propertyLocationId,
  onFolderChange,
}) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof SortableItem>('name');
  const [filterName, setFilterName] = useState('');

  const { data: hierarchy = [], isLoading: isHierarchyLoading } = useGetFolderHierarchy(
    currentFolderId || 0,
    {
      enabled: !!currentFolderId,
    }
  );

  const { data: rootContents, isLoading: isRootLoading } = useGetRootContents(
    { property_location_id: propertyLocationId ? Number(propertyLocationId) : undefined },
    { enabled: !currentFolderId && !!propertyLocationId }
  );

  const { data: folderContents, isLoading: isFolderLoading } = useGetFolderContents(
    currentFolderId ? Number(currentFolderId) : 0,
    { property_location_id: propertyLocationId ? Number(propertyLocationId) : undefined },
    { enabled: !!currentFolderId }
  );

  const contents = currentFolderId ? folderContents : rootContents;
  const isLoading = isHierarchyLoading || isRootLoading || isFolderLoading;

  // Document preview state
  const [previewDocumentId, setPreviewDocumentId] = useState<string | null>(null);
  const [previewDocumentCategoryId, setPreviewDocumentCategoryId] = useState<number | null>(null);
  const clickTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Drag and drop state
  const [draggedDocumentId, setDraggedDocumentId] = useState<string | null>(null);
  const [dragOverFolderId, setDragOverFolderId] = useState<number | null>(null);

  const moveDocumentsMutation = useMoveDocumentsToFolder();

  // Add new state for delete dialog
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<string | null>(null);

  // Add new state for folder delete dialog
  const [openDeleteFolderDialog, setOpenDeleteFolderDialog] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState<{
    id: number;
    name: string;
    documentCount: number;
  } | null>(null);

  // Add delete document mutation
  const deleteDocumentMutation = useDeleteVaultDocument({
    onSuccess: () => {
      // Invalidate the correct query keys based on whether we're in a folder or root
      if (currentFolderId) {
        queryClient.invalidateQueries({
          queryKey: [
            'folders',
            'contents',
            currentFolderId,
            {
              property_location_id: propertyLocationId,
            },
          ],
        });
        queryClient.invalidateQueries({
          queryKey: ['folders', 'hierarchy', currentFolderId],
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: [
            'folders',
            'root-contents',
            {
              property_location_id: propertyLocationId,
            },
          ],
        });
      }
      // Also invalidate all folder queries to ensure everything is up to date
      queryClient.invalidateQueries({ queryKey: ['folders'] });
    },
  });

  // Add delete folder mutation
  const deleteFolderMutation = useDeleteFolder();

  // Add move documents between properties mutation
  const moveDocumentsBetweenPropertiesMutation = useMoveDocumentsBetweenProperties();

  // Add move folders between properties mutation
  const moveFoldersBetweenPropertiesMutation = useMoveFoldersBetweenProperties();

  // Add new state for create folder modal
  const [isCreateFolderModalOpen, setIsCreateFolderModalOpen] = useState(false);

  // Context menu state
  const [contextMenu, setContextMenu] = useState<ContextMenuState>({
    mouseX: 0,
    mouseY: 0,
    item: null,
    open: false,
  });
  const [moveSubmenu, setMoveSubmenu] = useState<MoveSubmenuState>({
    open: false,
    anchorEl: null,
  });

  // Get all properties for move functionality
  const { data: allProperties = [] } = useGetAllPropertyLocations();

  // Add state for drag and drop upload
  const [isDragOverUpload, setIsDragOverUpload] = useState(false);
  const [uploadQueue, setUploadQueue] = useState<QueueItem[]>([]);
  const [currentUploadingFile, setCurrentUploadingFile] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);

  // Use ref to track if we're currently processing to avoid race conditions
  const isProcessingRef = useRef(false);

  // Track last drop time to prevent rapid duplicate drops
  const lastDropTimeRef = useRef<number>(0);

  // Track processed drop events to prevent duplicate processing
  const processedDropEventsRef = useRef<Set<string>>(new Set());

  // Track if we're currently processing a drop to prevent overlapping drops
  const isProcessingDropRef = useRef<boolean>(false);

  // Add a check for whether we're in a system folder
  const isInSystemFolder = useMemo(() => {
    if (!currentFolderId) return false;
    const currentFolder = hierarchy[hierarchy.length - 1];
    return currentFolder?.is_system_folder ?? false;
  }, [currentFolderId, hierarchy]);

  // Stable addFilesToQueue function to prevent duplicates
  const addFilesToQueue = useCallback((files: File[], targetFolderId: number | null = null) => {
    setUploadQueue(prevQueue => {
      // Filter out files that are already in the queue to prevent duplicates
      const newFiles = files.filter(
        file =>
          !prevQueue.some(
            queuedItem =>
              queuedItem.file.name === file.name &&
              queuedItem.file.size === file.size &&
              queuedItem.file.lastModified === file.lastModified &&
              queuedItem.targetFolderId === targetFolderId
          )
      );

      if (newFiles.length === 0) {
        return prevQueue;
      }

      const newQueueItems = newFiles.map(file => ({ file, targetFolderId }));
      return [...prevQueue, ...newQueueItems];
    });
  }, []);

  // Upload functionality with controlled queue processing
  const { uploadFile: uploadFileV2, isUploading: isUploadingV2 } = useDocumentUploadV2({
    onProgress: (response: UploadStreamResponse) => {
      setUploadProgress(response.progress ? response.progress * 100 : 0);
    },
    onComplete: (response: UploadStreamResponse) => {
      // Invalidate the correct query keys based on whether we're in a folder or root
      if (currentFolderId) {
        queryClient.invalidateQueries({
          queryKey: [
            'folders',
            'contents',
            currentFolderId,
            {
              property_location_id: propertyLocationId,
            },
          ],
        });
        queryClient.invalidateQueries({
          queryKey: ['folders', 'hierarchy', currentFolderId],
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: [
            'folders',
            'root-contents',
            {
              property_location_id: propertyLocationId,
            },
          ],
        });
      }

      // Also invalidate all folder queries to ensure everything is up to date
      queryClient.invalidateQueries({ queryKey: ['folders'] });

      // Clear current uploading file and reset progress
      setCurrentUploadingFile(null);
      setUploadProgress(0);
      isProcessingRef.current = false;

      // Ensure drag overlay is cleared when upload completes
      setIsDragOverUpload(false);
    },
    onError: (error: string) => {
      setCurrentUploadingFile(null);
      setUploadProgress(0);
      isProcessingRef.current = false;
      setUploadQueue([]); // Clear queue on error

      // Ensure drag overlay is cleared on error
      setIsDragOverUpload(false);

      enqueueSnackbar(error || 'Failed to upload file', { variant: 'error' });
    },
  });

  // Stable processNextFile function using useCallback with minimal dependencies
  const processNextFile = useCallback(() => {
    // Prevent multiple concurrent processing
    if (isProcessingRef.current || isUploadingV2) {
      return;
    }

    setUploadQueue(prevQueue => {
      if (prevQueue.length === 0) {
        return prevQueue;
      }

      const [itemToUpload, ...remainingItems] = prevQueue;
      const { file, targetFolderId } = itemToUpload;

      // Set processing flags before starting upload
      setCurrentUploadingFile(file.name);
      isProcessingRef.current = true;

      // Use the target folder ID from the queue item, fallback to currentFolderId
      const uploadFolderId = targetFolderId ?? currentFolderId;

      // Start the upload
      uploadFileV2({
        file,
        folder_id: uploadFolderId,
        location_ids: propertyLocationId ? [propertyLocationId] : undefined,
        tags: propertyLocationId
          ? {
            property_location_ids: [propertyLocationId],
          }
          : undefined,
      });

      return remainingItems;
    });
  }, [isUploadingV2, uploadFileV2, currentFolderId, propertyLocationId]);

  // Single useEffect to manage queue processing - only depends on queue length and upload state
  useEffect(() => {
    if (
      uploadQueue.length > 0 &&
      !isUploadingV2 &&
      !currentUploadingFile &&
      !isProcessingRef.current
    ) {
      // Use a small timeout to prevent rapid re-triggering
      const timeoutId = setTimeout(() => {
        processNextFile();
      }, 50);

      return () => clearTimeout(timeoutId);
    }
  }, [uploadQueue.length, isUploadingV2, currentUploadingFile, processNextFile]);

  // Reset drag overlay when uploads are complete
  useEffect(() => {
    if (
      uploadQueue.length === 0 &&
      !isUploadingV2 &&
      !currentUploadingFile &&
      !isProcessingRef.current &&
      isDragOverUpload
    ) {
      // Small delay to ensure drag events have finished processing
      const timeoutId = setTimeout(() => {
        setIsDragOverUpload(false);
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [uploadQueue.length, isUploadingV2, currentUploadingFile, isDragOverUpload]);

  const handleSort = (property: keyof SortableItem) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleFilterName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterName(event.target.value);
  };

  const sortedItems = useMemo(() => {
    if (!contents) return [];

    const allItems: SortableItem[] = [
      ...(contents.folders?.map(folder => ({
        id: folder.id,
        name: folder.name,
        created_at: folder.created_at,
        _count: folder._count,
        documentCount: folder._count?.documents || 0,
        type: 'folder' as const,
        is_system_folder: Boolean(folder.is_system_folder),
        created_by: folder.created_by,
        updated_at: folder.updated_at,
        lastModifiedDate: folder.lastModifiedDate,
        description: folder.description,
      })) || []),
      ...(contents.documents?.map(doc => ({
        id: doc.id,
        name: doc.original_name,
        created_at: doc.created_at,
        size: doc.size,
        documentCount: 0,
        type: 'document' as const,
        mime_type: doc.mime_type,
        original_name: doc.original_name,
        file_path: doc.file_path,
        status: doc.status,
        created_by: doc.created_by,
        updated_at: doc.updated_at,
        metadata: doc.metadata,
      })) || []),
    ];

    let filtered = allItems;

    // Apply name filter
    if (filterName) {
      filtered = filtered.filter((item: SortableItem) =>
        item.name.toLowerCase().includes(filterName.toLowerCase())
      );
    }

    return filtered.sort(getComparator(order, orderBy));
  }, [contents, order, orderBy, filterName]);

  const handleFolderClick = useCallback(
    (folderId: number | null) => {
      onFolderChange?.(folderId);
    },
    [onFolderChange]
  );

  const handleDocumentClick = useCallback((document: TDocument) => {
    if (clickTimerRef.current === null) {
      clickTimerRef.current = setTimeout(() => {
        setPreviewDocumentId(document.id);
        setPreviewDocumentCategoryId(document.category?.id ? Number(document.category.id) : null);
        clickTimerRef.current = null;
      }, 200);
    }
  }, []);

  const handleClosePreview = () => {
    setPreviewDocumentId(null);
    setPreviewDocumentCategoryId(null);
  };

  // Drag and drop handlers
  const handleDocumentDragStart = (event: React.DragEvent, documentId: string) => {
    event.dataTransfer.setData('application/terrai-document', documentId);
    setDraggedDocumentId(documentId);
  };

  const handleDocumentDragEnd = () => {
    setDraggedDocumentId(null);
    setDragOverFolderId(null);
  };

  const handleFolderDragOver = (event: React.DragEvent, folder: TDocumentFolder) => {
    // Only handle document moves, prevent file uploads at folder level
    if (event.dataTransfer.types.includes('application/terrai-document')) {
      event.preventDefault();
      event.stopPropagation();
      setDragOverFolderId(folder.id);
    } else if (
      event.dataTransfer.types.includes('Files') &&
      !event.dataTransfer.types.includes('application/terrai-document')
    ) {
      // For file drops on folder rows, preventDefault to enable dropping and stopPropagation to prevent bubbling
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const handleFolderDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOverFolderId(null);
  };

  // Updated handleFolderDrop to use the enhanced queue system with duplicate prevention
  const handleFolderDrop = async (event: React.DragEvent, folder: TDocumentFolder) => {
    event.preventDefault();
    event.stopPropagation(); // Always stop propagation
    setDragOverFolderId(null);

    const documentId = event.dataTransfer.getData('application/terrai-document');
    const hasFiles = event.dataTransfer.types.includes('Files');

    // Handle document moves (existing logic)
    if (documentId && !hasFiles) {
      try {
        await moveDocumentsMutation.mutateAsync({
          document_ids: [documentId],
          folder_id: folder.id,
        });

        // Invalidate queries for both source and destination
        await Promise.all([
          // Invalidate current folder contents
          currentFolderId
            ? queryClient.invalidateQueries({
              queryKey: [
                'folders',
                'contents',
                currentFolderId,
                {
                  property_location_id: propertyLocationId,
                },
              ],
            })
            : queryClient.invalidateQueries({
              queryKey: [
                'folders',
                'root-contents',
                {
                  property_location_id: propertyLocationId,
                },
              ],
            }),
          // Invalidate destination folder contents
          queryClient.invalidateQueries({
            queryKey: [
              'folders',
              'contents',
              folder.id,
              {
                property_location_id: propertyLocationId,
              },
            ],
          }),
          // Invalidate all folder queries to be safe
          queryClient.invalidateQueries({ queryKey: ['folders'] }),
        ]);

        enqueueSnackbar('Document moved successfully', { variant: 'success' });
      } catch (error) {
        enqueueSnackbar((error as Error).message || 'Failed to move document', {
          variant: 'error',
        });
      }
      return;
    }

    // Handle file uploads to the specific folder with enhanced duplicate prevention
    if (hasFiles && !documentId) {
      // Prevent overlapping drop processing
      if (isProcessingDropRef.current) {
        return;
      }

      const now = Date.now();

      // Prevent rapid duplicate drops (within 500ms)
      if (now - lastDropTimeRef.current < 500) {
        return;
      }
      lastDropTimeRef.current = now;
      isProcessingDropRef.current = true;

      const files = Array.from(event.dataTransfer.files);
      if (files.length === 0) {
        isProcessingDropRef.current = false;
        return;
      }

      // Create a unique identifier for this drop event based on files and target folder
      const dropEventId = `${folder.id}-${files
        .map(file => `${file.name}-${file.size}-${file.lastModified}`)
        .sort()
        .join('|')}`;

      // Check if we've already processed this exact drop event
      if (processedDropEventsRef.current.has(dropEventId)) {
        isProcessingDropRef.current = false;
        return;
      }

      // Mark this drop event as processed
      processedDropEventsRef.current.add(dropEventId);

      // Clean up old drop event IDs after 5 seconds to prevent memory leaks
      setTimeout(() => {
        processedDropEventsRef.current.delete(dropEventId);
      }, 5000);

      // Add files to queue with the specific target folder
      addFilesToQueue(files, folder.id);

      enqueueSnackbar(`${files.length} file(s) queued for upload to ${folder.name}`, {
        variant: 'success',
      });

      // Release the processing lock after a short delay
      setTimeout(() => {
        isProcessingDropRef.current = false;
      }, 100);
    }
  };

  // Helper function to determine border color
  const getBorderColor = (folder: TDocumentFolder, isDragOver: boolean) => {
    if (!isDragOver) return 'transparent';
    return folder.is_system_folder ? 'error.main' : 'primary.main';
  };

  // Updated handleFileDrop with enhanced duplicate prevention
  const handleFileDrop = async (event: React.DragEvent) => {
    // Only handle file uploads, not document moves
    if (
      event.dataTransfer.types.includes('Files') &&
      !event.dataTransfer.types.includes('application/terrai-document')
    ) {
      event.preventDefault();
      event.stopPropagation();

      // Prevent overlapping drop processing
      if (isProcessingDropRef.current) {
        setIsDragOverUpload(false);
        return;
      }

      const now = Date.now();

      // Prevent rapid duplicate drops (within 500ms)
      if (now - lastDropTimeRef.current < 500) {
        setIsDragOverUpload(false);
        return;
      }
      lastDropTimeRef.current = now;

      setIsDragOverUpload(false);
      isProcessingDropRef.current = true;

      const files = Array.from(event.dataTransfer.files);

      if (files.length === 0) {
        isProcessingDropRef.current = false;
        return;
      }

      // Create a unique identifier for this drop event based on files
      const dropEventId = files
        .map(file => `${file.name}-${file.size}-${file.lastModified}`)
        .sort()
        .join('|');

      // Check if we've already processed this exact drop event
      if (processedDropEventsRef.current.has(dropEventId)) {
        isProcessingDropRef.current = false;
        return;
      }

      // Mark this drop event as processed
      processedDropEventsRef.current.add(dropEventId);

      // Clean up old drop event IDs after 5 seconds to prevent memory leaks
      setTimeout(() => {
        processedDropEventsRef.current.delete(dropEventId);
      }, 5000);

      // Add files to queue with null target folder (current folder)
      addFilesToQueue(files, null);

      // Release the processing lock after a short delay
      setTimeout(() => {
        isProcessingDropRef.current = false;
      }, 100);
    }
  };

  // File upload drag and drop handlers
  const handleFileDragOver = (event: React.DragEvent) => {
    // Only allow file drops, not document moves
    if (
      event.dataTransfer.types.includes('Files') &&
      !event.dataTransfer.types.includes('application/terrai-document')
    ) {
      event.preventDefault();
      event.stopPropagation();
      setIsDragOverUpload(true);
    }
  };

  const handleFileDragLeave = (event: React.DragEvent) => {
    // Only handle file uploads, not document moves
    if (
      event.dataTransfer.types.includes('Files') &&
      !event.dataTransfer.types.includes('application/terrai-document')
    ) {
      event.preventDefault();
      event.stopPropagation();

      // Simplified drag leave - just reset the state
      // The dragOver event will set it back to true if still dragging over
      setIsDragOverUpload(false);
    }
  };

  // Helper to format date
  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // Helper to get folder name by ID
  const getFolderNameById = (folderId: number | null) => {
    if (!folderId) return 'Current Folder';
    const targetFolder = sortedItems.find(item => item.type === 'folder' && item.id === folderId);
    return targetFolder?.name || 'Unknown Folder';
  };

  // Add delete handlers
  const handleDeleteClick = useCallback((event: React.MouseEvent, documentId: string) => {
    event.stopPropagation();
    setDocumentToDelete(documentId);
    setOpenDeleteDialog(true);
  }, []);

  const handleDeleteConfirm = () => {
    if (documentToDelete) {
      deleteDocumentMutation.mutate({ documentId: documentToDelete });
    }
    setOpenDeleteDialog(false);
    setDocumentToDelete(null);
  };

  const handleDeleteCancel = () => {
    setOpenDeleteDialog(false);
    setDocumentToDelete(null);
  };

  // Add folder delete handlers
  const handleFolderDeleteClick = useCallback(
    (event: React.MouseEvent, folder: TDocumentFolder) => {
      event.stopPropagation();
      setFolderToDelete({
        id: folder.id,
        name: folder.name,
        documentCount: folder._count?.documents || 0,
      });
      setOpenDeleteFolderDialog(true);
    },
    []
  );

  const handleFolderDeleteConfirm = async () => {
    if (folderToDelete) {
      try {
        await deleteFolderMutation.mutateAsync(folderToDelete.id);

        // Invalidate queries after successful deletion
        if (currentFolderId) {
          queryClient.invalidateQueries({
            queryKey: [
              'folders',
              'contents',
              currentFolderId,
              {
                property_location_id: propertyLocationId,
              },
            ],
          });
          queryClient.invalidateQueries({
            queryKey: ['folders', 'hierarchy', currentFolderId],
          });
        } else {
          queryClient.invalidateQueries({
            queryKey: [
              'folders',
              'root-contents',
              {
                property_location_id: propertyLocationId,
              },
            ],
          });
        }
        // Also invalidate all folder queries to ensure everything is up to date
        queryClient.invalidateQueries({ queryKey: ['folders'] });

        enqueueSnackbar('Folder deleted successfully', { variant: 'success' });
      } catch (error) {
        enqueueSnackbar((error as Error).message || 'Failed to delete folder', {
          variant: 'error',
        });
      }
    }
    setOpenDeleteFolderDialog(false);
    setFolderToDelete(null);
  };

  const handleFolderDeleteCancel = () => {
    setOpenDeleteFolderDialog(false);
    setFolderToDelete(null);
  };

  // Add handlers for create folder modal
  const handleOpenCreateFolderModal = () => {
    setIsCreateFolderModalOpen(true);
  };

  const handleCloseCreateFolderModal = () => {
    setIsCreateFolderModalOpen(false);
  };

  // Context menu handlers
  const handleContextMenu = useCallback((event: React.MouseEvent, item: SortableItem) => {
    event.preventDefault();
    event.stopPropagation();
    setContextMenu({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
      item,
      open: true,
    });
  }, []);

  const handleContextMenuClose = useCallback(() => {
    setContextMenu(prev => ({
      ...prev,
      open: false,
      item: null,
    }));
    setMoveSubmenu({
      open: false,
      anchorEl: null,
    });
  }, []);

  const handleViewItem = useCallback(() => {
    if (!contextMenu.item) return;

    if (contextMenu.item.type === 'folder') {
      handleFolderClick(contextMenu.item.id as number);
    } else {
      const document: TDocument = {
        id: contextMenu.item.id as string,
        original_name: contextMenu.item.original_name || '',
        created_at: contextMenu.item.created_at,
        size: contextMenu.item.size || 0,
        mime_type: contextMenu.item.mime_type || '',
        file_path: contextMenu.item.file_path || '',
        status: contextMenu.item.status as TDocumentStatus,
        created_by: contextMenu.item.created_by,
        updated_at: contextMenu.item.updated_at,
        category: undefined,
        category_id: undefined,
        progress: undefined,
        metadata: contextMenu.item.metadata,
      };
      handleDocumentClick(document);
    }
    handleContextMenuClose();
  }, [contextMenu.item, handleFolderClick, handleDocumentClick, handleContextMenuClose]);

  const handleMoveSubmenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setMoveSubmenu({
      open: true,
      anchorEl: event.currentTarget,
    });
  }, []);

  const handleMoveSubmenuClose = useCallback(() => {
    setMoveSubmenu({
      open: false,
      anchorEl: null,
    });
  }, []);

  const handleMoveToProperty = useCallback(
    async (targetPropertyId: number) => {
      if (!contextMenu.item) return;

      // Check if trying to move to the same property
      if (targetPropertyId === propertyLocationId) {
        enqueueSnackbar('Item is already in this property', { variant: 'info' });
        handleContextMenuClose();
        return;
      }

      if (!propertyLocationId) {
        enqueueSnackbar('Source property location is required', { variant: 'error' });
        handleContextMenuClose();
        return;
      }

      try {
        if (contextMenu.item.type === 'document') {
          // Handle document move
          const result = await moveDocumentsBetweenPropertiesMutation.mutateAsync({
            document_ids: [contextMenu.item.id as string],
            source_property_location_id: propertyLocationId,
            target_property_location_id: targetPropertyId,
          });

          const targetProperty = allProperties.find(p => p.id === targetPropertyId);
          const targetPropertyName = targetProperty?.name || `Property ${targetPropertyId}`;

          enqueueSnackbar(
            `Document successfully moved to ${targetPropertyName}. ${result.moved_documents_count} document(s) moved, ${result.updated_calendar_events_count} calendar event(s) updated.`,
            { variant: 'success' }
          );
        } else {
          // Handle folder move
          const result = await moveFoldersBetweenPropertiesMutation.mutateAsync({
            folder_ids: [contextMenu.item.id as number],
            source_property_location_id: propertyLocationId,
            target_property_location_id: targetPropertyId,
          });

          const targetProperty = allProperties.find(p => p.id === targetPropertyId);
          const targetPropertyName = targetProperty?.name || `Property ${targetPropertyId}`;

          enqueueSnackbar(
            `Folder successfully moved to ${targetPropertyName}. ${result.moved_folders_count} folder(s) moved, ${result.moved_documents_count} document(s) moved, ${result.updated_calendar_events_count} calendar event(s) updated.`,
            { variant: 'success' }
          );
        }

        // Invalidate queries to refresh the current view
        if (currentFolderId) {
          queryClient.invalidateQueries({
            queryKey: [
              'folders',
              'contents',
              currentFolderId,
              {
                property_location_id: propertyLocationId,
              },
            ],
          });
        } else {
          queryClient.invalidateQueries({
            queryKey: [
              'folders',
              'root-contents',
              {
                property_location_id: propertyLocationId,
              },
            ],
          });
        }

        // Also invalidate queries for the target property
        queryClient.invalidateQueries({
          queryKey: [
            'folders',
            'root-contents',
            {
              property_location_id: targetPropertyId,
            },
          ],
        });
      } catch (error) {
        enqueueSnackbar(
          (error as Error).message || `Failed to move ${contextMenu.item.type} between properties`,
          {
            variant: 'error',
          }
        );
      }

      handleContextMenuClose();
    },
    [
      contextMenu.item,
      propertyLocationId,
      handleContextMenuClose,
      moveDocumentsBetweenPropertiesMutation,
      moveFoldersBetweenPropertiesMutation,
      allProperties,
      queryClient,
      currentFolderId,
    ]
  );

  const handleDeleteFromContext = useCallback(() => {
    if (!contextMenu.item) return;

    if (contextMenu.item.type === 'document') {
      setDocumentToDelete(contextMenu.item.id as string);
      setOpenDeleteDialog(true);
    } else {
      const folder: TDocumentFolder = {
        id: contextMenu.item.id as number,
        name: contextMenu.item.name,
        created_at: contextMenu.item.created_at,
        _count: { documents: contextMenu.item._count?.documents || 0, sub_folders: 0 },
        is_system_folder: Boolean(contextMenu.item.is_system_folder),
        created_by: contextMenu.item.created_by,
        updated_at: contextMenu.item.updated_at,
        description: contextMenu.item.description || '',
        parent_folder_id: undefined,
        property_location_id: undefined,
      };
      setFolderToDelete({
        id: folder.id,
        name: folder.name,
        documentCount: folder._count?.documents || 0,
      });
      setOpenDeleteFolderDialog(true);
    }

    handleContextMenuClose();
  }, [contextMenu.item, handleContextMenuClose]);

  // Get available properties for move
  const availableProperties = useMemo(
    () =>
      // Return all properties for selection
      allProperties.map(property => ({
        id: property.id,
        name: property.name || property.address_text,
        address_text: property.address_text,
        isCurrentProperty: property.id === propertyLocationId,
      })),
    [allProperties, propertyLocationId]
  );

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <MuiPageWrapper>
      <Stack
        direction="column"
        spacing={2}
        sx={{ flex: '1 1 100%', position: 'relative' }}
        onDragOver={handleFileDragOver}
        onDragLeave={handleFileDragLeave}
        onDrop={handleFileDrop}
      >
        {/* Header with breadcrumbs and actions */}
        <Paper elevation={0} sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
            <Breadcrumbs separator={<ArrowRightIcon />} sx={{ flexGrow: 1 }}>
              <Button
                variant="text"
                onClick={() => handleFolderClick(null)}
                startIcon={<FolderIcon />}
                size="small"
                sx={{ borderRadius: 2 }}
              >
                My Vault
              </Button>
              {hierarchy.map((folder: TDocumentFolder, index: number) => {
                const isLast = index === hierarchy.length - 1;
                if (isLast) {
                  return (
                    <Typography key={folder.id} color="primary" variant="body1" fontWeight={600}>
                      {folder.name}
                    </Typography>
                  );
                }
                return (
                  <Button
                    key={folder.id}
                    variant="text"
                    onClick={() => handleFolderClick(folder.id)}
                    startIcon={<FolderIcon />}
                    size="small"
                    sx={{ borderRadius: 2 }}
                  >
                    {folder.name}
                  </Button>
                );
              })}
            </Breadcrumbs>
            <Tooltip
              title={
                isInSystemFolder
                  ? 'Cannot create folders inside system folders'
                  : 'Create new folder'
              }
            >
              <span>
                <IconButton
                  color="primary"
                  onClick={handleOpenCreateFolderModal}
                  disabled={isInSystemFolder}
                  sx={{
                    backgroundColor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: 'grey.300',
                      color: 'grey.500',
                    },
                  }}
                >
                  <FolderPlusIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Paper>

        <Box sx={{ flex: 1 }}>
          <Toolbar
            sx={{
              height: 64,
              display: 'flex',
              justifyContent: 'space-between',
              p: theme.spacing(2),
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: 'common.white',
            }}
          >
            <OutlinedInput
              fullWidth
              value={filterName}
              onChange={handleFilterName}
              placeholder="Search files and folders..."
              startAdornment={
                <InputAdornment position="start">
                  <SearchMdIcon sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              }
              sx={{
                maxWidth: 320,
                bgcolor: 'common.white',
              }}
            />
          </Toolbar>

          {/* Table View */}
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: 'none',
              border: '0px solid',
              borderColor: 'divider',
              bgcolor: 'common.white',
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: theme.palette.secondary[200] }}>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      color: 'text.primary',
                      fontSize: '0.875rem',
                      cursor: 'pointer',
                      textTransform: 'none',
                      minWidth: 400,
                      '&:hover': { bgcolor: `${theme.palette.secondary[200]} !important` },
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === 'name'}
                      direction={orderBy === 'name' ? order : 'asc'}
                      onClick={() => handleSort('name')}
                    >
                      Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      color: 'text.primary',
                      fontSize: '0.875rem',
                      cursor: 'pointer',
                      textTransform: 'none',
                      minWidth: 120,
                      '&:hover': { bgcolor: `${theme.palette.secondary[200]} !important` },
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === 'type'}
                      direction={orderBy === 'type' ? order : 'asc'}
                      onClick={() => handleSort('type')}
                    >
                      Type
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      color: 'text.primary',
                      fontSize: '0.875rem',
                      cursor: 'pointer',
                      textTransform: 'none',
                      minWidth: 160,
                      '&:hover': { bgcolor: `${theme.palette.secondary[200]} !important` },
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === 'lastModifiedDate'}
                      direction={orderBy === 'lastModifiedDate' ? order : 'asc'}
                      onClick={() => handleSort('lastModifiedDate')}
                    >
                      Last Modified
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{
                      fontWeight: 'bold',
                      color: 'text.primary',
                      fontSize: '0.875rem',
                      cursor: 'pointer',
                      textTransform: 'none',
                      minWidth: 80,
                      '&:hover': { bgcolor: `${theme.palette.secondary[200]} !important` },
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === 'documentCount'}
                      direction={orderBy === 'documentCount' ? order : 'asc'}
                      onClick={() => handleSort('documentCount')}
                    >
                      Files
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{
                      fontWeight: 'bold',
                      color: 'text.primary',
                      fontSize: '0.875rem',
                      cursor: 'pointer',
                      textTransform: 'none',
                      minWidth: 100,
                      '&:hover': { bgcolor: `${theme.palette.secondary[200]} !important` },
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === 'size'}
                      direction={orderBy === 'size' ? order : 'asc'}
                      onClick={() => handleSort('size')}
                    >
                      File Size
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="left" sx={{ width: 60 }} /> {/* Actions column */}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 8 }}>
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <FolderIcon sx={{ fontSize: 64, color: 'grey.300', mb: 2 }} />
                        <Typography variant="h6" color="textSecondary" gutterBottom>
                          {filterName
                            ? 'No items found matching your search.'
                            : 'This folder is empty'}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" textAlign="center">
                          {filterName
                            ? 'Try adjusting your search terms.'
                            : 'Drag and drop documents here or create a new folder to get started'}
                        </Typography>
                        
                        {/* Smart Suggestions based on folder name */}
                        {!filterName && currentFolderId && hierarchy.length > 0 && (
                          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2, maxWidth: 400 }}>
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                              💡 Suggestions for this folder:
                            </Typography>
                            {(() => {
                              const folderName = hierarchy[hierarchy.length - 1]?.name?.toLowerCase() || '';
                              
                              if (folderName.includes('financial') || folderName.includes('finance')) {
                                return (
                                  <Typography variant="body2" color="text.secondary">
                                    Upload bills, invoices, receipts, bank statements, and 
                                    financial reports here
                                  </Typography>
                                );
                              } if (folderName.includes('legal') || folderName.includes('ownership')) {
                                return (
                                  <Typography variant="body2" color="text.secondary">
                                    Upload leases, contracts, deeds, agreements, and legal 
                                    documents here
                                  </Typography>
                                );
                              } if (folderName.includes('maintenance') || folderName.includes('repair')) {
                                return (
                                  <Typography variant="body2" color="text.secondary">
                                    Upload work orders, repair receipts, maintenance schedules, 
                                    and inspection reports here
                                  </Typography>
                                );
                              } if (folderName.includes('tenant')) {
                                return (
                                  <Typography variant="body2" color="text.secondary">
                                    Upload tenant applications, background checks, move-in forms, 
                                    and correspondence here
                                  </Typography>
                                );
                              } if (folderName.includes('insurance')) {
                                return (
                                  <Typography variant="body2" color="text.secondary">
                                    Upload insurance policies, claims, declarations pages, and 
                                    coverage documents here
                                  </Typography>
                                );
                              } 
                              return (
                                <Typography variant="body2" color="text.secondary">
                                    Upload any relevant documents for 
                                  {hierarchy[hierarchy.length - 1]?.name}
                                </Typography>
                              );
                              
                            })()}
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedItems.map(item => {
                    if (item.type === 'folder') {
                      const folder: TDocumentFolder = {
                        id: item.id as number,
                        name: item.name,
                        created_at: item.created_at,
                        _count: { documents: item._count?.documents || 0, sub_folders: 0 },
                        is_system_folder: Boolean(item.is_system_folder),
                        created_by: item.created_by,
                        updated_at: item.updated_at,
                        description: item.description || '',
                        parent_folder_id: undefined,
                        property_location_id: undefined,
                      };
                      return (
                        <TableRow
                          key={`folder-${folder.id}`}
                          onClick={() => handleFolderClick(folder.id)}
                          onContextMenu={e => handleContextMenu(e, item)}
                          onDragOver={e => handleFolderDragOver(e, folder)}
                          onDragLeave={handleFolderDragLeave}
                          onDrop={e => handleFolderDrop(e, folder)}
                          sx={{
                            cursor: 'pointer',
                            backgroundColor:
                              dragOverFolderId === folder.id ? alpha('#2196f3', 0.08) : 'inherit',
                            transition: 'all 0.2s ease-in-out',
                            border:
                              dragOverFolderId === folder.id
                                ? '2px dashed'
                                : '2px solid transparent',
                            borderColor: getBorderColor(folder, dragOverFolderId === folder.id),
                            '&:hover': {
                              backgroundColor: `${theme.palette.secondary[200]} !important`,
                              '& .MuiTableCell-root': {
                                color: 'inherit !important',
                              },
                              '& .MuiSvgIcon-root': {
                                color: 'inherit !important',
                              },
                            },
                            borderBottom: '1px solid',
                            borderBottomColor: 'divider',
                          }}
                        >
                          <TableCell>
                            <Stack direction="row" spacing={2} alignItems="center">
                              <FileIconWithOverlay
                                FileIcon={folder.is_system_folder ? FolderShieldIcon : FolderIcon}
                                description={folder.description}
                              />
                              <Tooltip
                                title={(() => {
                                  const folderName = folder.name.toLowerCase();
                                  if (folder._count?.documents === 0) {
                                    if (folderName.includes('financial') || folderName.includes('finance')) {
                                      return 'Upload bills, invoices, receipts, and financial documents here';
                                    } if (folderName.includes('legal') || folderName.includes('ownership')) {
                                      return 'Upload leases, contracts, deeds, and legal documents here';
                                    } if (folderName.includes('maintenance') || folderName.includes('repair')) {
                                      return 'Upload work orders, repair receipts, and maintenance documents here';
                                    } if (folderName.includes('tenant')) {
                                      return 'Upload tenant applications and correspondence here';
                                    } if (folderName.includes('insurance')) {
                                      return 'Upload insurance policies and claims here';
                                    }
                                    return 'Click to open folder or drag documents here';
                                  }
                                  return `${folder._count?.documents || 0} documents in this folder`;
                                })()}
                                placement="right"
                                arrow
                              >
                                <Typography
                                  variant="body3"
                                  color="text.primary"
                                  fontWeight={500}
                                  sx={{
                                    maxWidth: 500,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    '&:hover': {
                                      color: theme.palette.primary.main,
                                      textDecoration: 'underline',
                                    },
                                  }}
                                >
                                  {folder.name}
                                </Typography>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body3" color="text.primary">
                              Folder
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body3" color="text.primary">
                              {formatDate(item.lastModifiedDate || folder.created_at)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body3" color="text.primary">
                              {folder._count?.documents || 0}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body3" color="text.primary">
                              —
                            </Typography>
                          </TableCell>
                          <TableCell align="left">
                            {!folder.is_system_folder && (
                              <IconButton
                                size="small"
                                onClick={e => handleFolderDeleteClick(e, folder)}
                                sx={{
                                  color: 'error.main',
                                  '&:hover': {
                                    backgroundColor: alpha(theme.palette.error.main, 0.08),
                                  },
                                }}
                              >
                                <Trash01Icon />
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    }
                    const document: TDocument = {
                      id: item.id as string,
                      original_name: item.original_name || '',
                      created_at: item.created_at,
                      size: item.size || 0,
                      mime_type: item.mime_type || '',
                      file_path: item.file_path || '',
                      status: item.status as TDocumentStatus,
                      created_by: item.created_by,
                      updated_at: item.updated_at,
                      category: undefined,
                      category_id: undefined,
                      progress: undefined,
                      metadata: item.metadata,
                    };
                    let FileIcon: React.ComponentType = DocumentIcon;
                    // Check file type for appropriate icon
                    if (document.mime_type?.startsWith('image/')) {
                      FileIcon = Image01Icon;
                    } else if (document.mime_type === 'application/pdf') {
                      FileIcon = File07Icon;
                    }

                    return (
                      <TableRow
                        key={`document-${document.id}`}
                        onClick={() => handleDocumentClick(document)}
                        onContextMenu={e => handleContextMenu(e, item)}
                        draggable
                        onDragStart={e => handleDocumentDragStart(e, document.id)}
                        onDragEnd={handleDocumentDragEnd}
                        sx={{
                          cursor: 'pointer',
                          opacity: draggedDocumentId === document.id ? 0.5 : 1,
                          '&:hover': {
                            backgroundColor: `${theme.palette.secondary[200]} !important`,
                            '& .MuiTableCell-root': {
                              color: 'inherit !important',
                            },
                            '& .MuiSvgIcon-root': {
                              color: 'inherit !important',
                            },
                          },
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <TableCell>
                          <Stack direction="row" spacing={2} alignItems="center">
                            <FileIconWithOverlay FileIcon={FileIcon} metadata={document.metadata} />
                            <Tooltip
                              title={
                                document.metadata?.google_drive_id
                                  ? `${document.original_name} (Shared from Google Drive by ${
                                    document.metadata.shared_by_email || 'unknown'
                                  })`
                                  : document.original_name
                              }
                              placement="right"
                            >
                              <Typography
                                variant="body3"
                                color="text.primary"
                                fontWeight={500}
                                sx={{
                                  maxWidth: 500,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  '&:hover': {
                                    color: theme.palette.primary.main,
                                    textDecoration: 'underline',
                                  },
                                }}
                              >
                                {document.original_name}
                              </Typography>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body3" color="text.primary">
                            {document.mime_type}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body3" color="text.primary">
                            {formatDate(document.created_at)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body3" color="text.primary">
                            —
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body3" color="text.primary">
                            {document.size ? `${Math.round(document.size / 1024)} KB` : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="left">
                          <IconButton
                            size="small"
                            onClick={e => handleDeleteClick(e, document.id)}
                            sx={{
                              color: 'error.main',
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.error.main, 0.08),
                              },
                            }}
                          >
                            <Trash01Icon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Updated Upload Progress */}
        {(currentUploadingFile || uploadQueue.length > 0) && (
          <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Uploading Files
            </Typography>

            {/* Current uploading file */}
            {currentUploadingFile && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  Currently uploading: {currentUploadingFile}
                  {uploadQueue.length > 0 && uploadQueue[0]?.targetFolderId && (
                    <> to {getFolderNameById(uploadQueue[0].targetFolderId)}</>
                  )}
                </Typography>
                <LinearProgress variant="determinate" value={uploadProgress} sx={{ mt: 0.5 }} />
                <Typography variant="caption" color="textSecondary">
                  {Math.round(uploadProgress)}%
                </Typography>
              </Box>
            )}

            {/* Upload queue */}
            {uploadQueue.length > 0 && (
              <Box>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  {uploadQueue.length} file{uploadQueue.length > 1 ? 's' : ''} in queue:
                </Typography>
                {uploadQueue.slice(0, 3).map(item => {
                  const targetFolderName = getFolderNameById(item.targetFolderId);

                  return (
                    <Typography
                      key={`${item.file.name}-${item.file.size}-${item.file.lastModified}`}
                      variant="caption"
                      color="textSecondary"
                      display="block"
                    >
                      • {item.file.name} → {targetFolderName}
                    </Typography>
                  );
                })}
                {uploadQueue.length > 3 && (
                  <Typography variant="caption" color="textSecondary" display="block">
                    ... and {uploadQueue.length - 3} more
                  </Typography>
                )}
              </Box>
            )}
          </Paper>
        )}

        {/* Drag and Drop Overlay */}
        {isDragOverUpload && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: alpha(
                isInSystemFolder ? theme.palette.error.main : theme.palette.primary.main,
                0.1
              ),
              border: `2px dashed ${
                isInSystemFolder ? theme.palette.error.main : theme.palette.primary.main
              }`,
              borderRadius: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              pointerEvents: 'none',
            }}
          >
            <UploadCloud01Icon
              sx={{
                fontSize: 64,
                color: isInSystemFolder ? 'error.main' : 'primary.main',
                mb: 2,
              }}
            />
            <Typography
              variant="h5"
              color={isInSystemFolder ? 'error' : 'primary'}
              fontWeight={600}
              gutterBottom
            >
              Drop files here to upload
            </Typography>
            <Typography variant="body1" color={isInSystemFolder ? 'error' : 'primary'}>
              {(() => {
                if (isInSystemFolder) {
                  return 'System folders are read-only';
                }
                if (currentFolderId) {
                  const folderName = hierarchy[hierarchy.length - 1]?.name || 'this folder';
                  return `Files will be uploaded to ${folderName}`;
                }
                return 'Files will be uploaded to My Vault';
              })()}
            </Typography>
          </Box>
        )}

        {/* Document Preview Modal */}
        {previewDocumentId && (
          <DocumentViewModal
            id={previewDocumentId}
            categoryId={previewDocumentCategoryId ?? undefined}
            onClose={handleClosePreview}
          />
        )}

        {/* Add Delete Confirmation Dialog */}
        <Dialog
          open={openDeleteDialog}
          onClose={handleDeleteCancel}
          aria-labelledby="delete-dialog-title"
          PaperProps={{
            sx: {
              minWidth: { xs: '90%', sm: 400 },
              maxWidth: { xs: '90%', sm: 500 },
            },
          }}
        >
          <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to delete this document?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel} color="primary">
              Cancel
            </Button>
            <Button onClick={handleDeleteConfirm} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Folder Delete Confirmation Dialog */}
        <Dialog
          open={openDeleteFolderDialog}
          onClose={handleFolderDeleteCancel}
          aria-labelledby="delete-folder-dialog-title"
          PaperProps={{
            sx: {
              minWidth: { xs: '90%', sm: 400 },
              maxWidth: { xs: '90%', sm: 500 },
            },
          }}
        >
          <DialogTitle id="delete-folder-dialog-title">Confirm Delete</DialogTitle>
          <DialogContent>
            <Stack spacing={2}>
              <Typography>
                Are you sure you want to delete the folder "{folderToDelete?.name}"?
              </Typography>
              {folderToDelete && folderToDelete.documentCount > 0 && (
                <Typography color="warning.main" variant="body2">
                  ⚠️ This folder contains {folderToDelete.documentCount} file
                  {folderToDelete.documentCount !== 1 ? 's' : ''}. All files inside this folder will
                  also be permanently deleted.
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary">
                This action cannot be undone.
              </Typography>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleFolderDeleteCancel} color="primary">
              Cancel
            </Button>
            <Button
              onClick={handleFolderDeleteConfirm}
              color="error"
              variant="contained"
              disabled={deleteFolderMutation.isPending}
            >
              {deleteFolderMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Create Folder Modal */}
        <CreateFolderModal
          open={isCreateFolderModalOpen}
          onClose={handleCloseCreateFolderModal}
          currentFolderId={currentFolderId}
          propertyLocationId={propertyLocationId}
        />

        {/* Context Menu */}
        <Menu
          open={contextMenu.open}
          onClose={handleContextMenuClose}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu.open
              ? {
                top: contextMenu.mouseY,
                left: contextMenu.mouseX,
              }
              : undefined
          }
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          slotProps={{
            paper: {
              sx: {
                minWidth: 180,
                boxShadow: theme.shadows[8],
              },
            },
          }}
        >
          <MenuItem onClick={handleViewItem}>
            <ListItemIcon>
              <EyeIcon />
            </ListItemIcon>
            <ListItemText>
              {contextMenu.item?.type === 'folder' ? 'Open Folder' : 'View Document'}
            </ListItemText>
          </MenuItem>

          {/* Only show Move option for non-system folders */}
          {!contextMenu.item?.is_system_folder && (
            <MenuItem onClick={handleMoveSubmenuOpen}>
              <ListItemIcon>
                <MoveIcon />
              </ListItemIcon>
              <ListItemText>Move to</ListItemText>
              <ArrowRightIcon sx={{ ml: 1, fontSize: 16 }} />
            </MenuItem>
          )}

          {/* Only show divider if Move or Delete options are available */}
          {!contextMenu.item?.is_system_folder && <Divider />}

          {/* Only show Delete option for non-system folders */}
          {!contextMenu.item?.is_system_folder && (
            <MenuItem onClick={handleDeleteFromContext} sx={{ color: 'error.main' }}>
              <ListItemIcon>
                <Trash01Icon sx={{ color: 'error.main' }} />
              </ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </MenuItem>
          )}
        </Menu>

        {/* Move Submenu */}
        <Menu
          open={moveSubmenu.open}
          onClose={handleMoveSubmenuClose}
          anchorEl={moveSubmenu.anchorEl}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          slotProps={{
            paper: {
              sx: {
                minWidth: 200,
                maxHeight: 300,
                overflowY: 'auto',
                boxShadow: theme.shadows[8],
              },
            },
          }}
        >
          {availableProperties.map(property => (
            <MenuItem
              key={property.id}
              onClick={() => handleMoveToProperty(property.id)}
              disabled={property.isCurrentProperty}
              sx={{
                opacity: property.isCurrentProperty ? 0.5 : 1,
              }}
            >
              <ListItemIcon>
                <Building03Icon />
              </ListItemIcon>
              <ListItemText
                primary={property.name}
                secondary={
                  property.isCurrentProperty ? '(Current property)' : property.address_text
                }
              />
            </MenuItem>
          ))}
        </Menu>
      </Stack>
    </MuiPageWrapper>
  );
};

export default FolderView;
